import React, { useState, useEffect, useRef } from 'react';
import { Layout, Button, Typography, Collapse, Input } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { Container } from 'react-bootstrap';
import DashboardShot from "../../assets/images/app-screens/dashboard.png";
import CalendarShot from "../../assets/images/app-screens/calendar.png";
import ResearchShot from "../../assets/images/app-screens/research-assistant.png";
import WritingShot from "../../assets/images/app-screens/writing-assistant.png";
import ReportShot from "../../assets/images/app-screens/report.png";
import CameraIcon from "../../assets/images/camera-icon.svg";
import CalenderIcon from "../../assets/images/calender-icon.svg";
import DocumentIcon from "../../assets/images/document-icon.svg";
import ControlIcon from "../../assets/images/control-icon.svg";
import DesignShape from "../../assets/images/blog-shape.png";
import FooterIndex from '../Auth/Footer';
import HeaderIndex from '../Auth/Header';

const { Content } = Layout;
const { Text } = Typography;
const { Panel } = Collapse;

function Reveal({ children, className = '', delay = 0, as: Tag = 'div', style = {} }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(node); } },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
    >
      {children}
    </Tag>
  );
}

function ScrollStory({ steps }) {
  const [active, setActive] = useState(0);
  const stepRefs = useRef([]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(Number(entry.target.dataset.storyIdx));
        });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );
    stepRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const current = steps[active];
  return (
    <div className="scroll-story">
      <div className="scroll-story-text">
        {steps.map((step, i) => (
          <div
            key={step.key}
            ref={(el) => (stepRefs.current[i] = el)}
            data-story-idx={i}
            className={`scroll-story-step ${active === i ? 'is-active' : ''}`}
          >
            <span className="scroll-story-step-index">{String(i + 1).padStart(2, '0')}</span>
            <h4>{step.title}</h4>
            <p>{step.desc}</p>
            <div className="scroll-story-step-shot">
              <div className="browser-frame">
                <div className="browser-frame-bar">
                  <div className="browser-frame-dots"><span></span><span></span><span></span></div>
                  <div className="browser-frame-url">{step.url}</div>
                </div>
                <img src={step.img} alt={step.title} className="browser-frame-shot" loading="lazy" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="scroll-story-visual">
        <div className="browser-frame">
          <div className="browser-frame-bar">
            <div className="browser-frame-dots"><span></span><span></span><span></span></div>
            <div className="browser-frame-url">{current.url}</div>
          </div>
          <img src={current.img} alt={current.title} className="browser-frame-shot" loading="lazy" />
        </div>
      </div>
    </div>
  );
}

export default function ImergeyWebsite() {
  const [trialEmail, setTrialEmail] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  const goToSignup = (email) => {
    const base = process.env.REACT_APP_CLIENT_SIGNUP_URL;
    const url = email ? `${base}?email=${encodeURIComponent(email)}` : base;
    window.open(url, '_blank');
  };

  const goToDemo = () => {
    window.location.href = '/book-demo';
  };

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const faqData = [
    {
      question: "What is IMERGEY?",
      answer: "IMERGEY is an AI-powered content platform for brand and marketing teams. Plan, research, write, schedule, and report on your content — all from one shared calendar."
    },
    {
      question: "Do I still need separate AI tools for research and writing?",
      answer: "No. IMERGEY's built-in Research Assistant and Writing Assistant take you from a topic to a publish-ready post without ever leaving the app."
    },
    {
      question: "Can my whole team work in the same calendar?",
      answer: "Yes. Invite teammates, assign role-based permissions, and route posts through approval before anything goes live — all on one shared calendar."
    },
    {
      question: "Which platforms can I publish to from IMERGEY?",
      answer: "IMERGEY connects directly to LinkedIn today, with more channels on the roadmap, so you can manage your most important platform from a single dashboard."
    },
    {
      question: "Is there a free trial?",
      answer: "Yes — start a free trial with no credit card required and explore every feature, including the AI assistants and reporting, before you commit."
    }
  ];

  const storySteps = [
    { key: 'dashboard', title: 'Get the full picture. Total control', desc: "KPIs, calendar, activity — all in one unified dashboard.", url: 'imergey.ai/dashboard', img: DashboardShot },
    { key: 'calendar', title: 'Plan for weeks in one view — instantly', desc: 'Schedule and approve posts without leaving the calendar.', url: 'imergey.ai/calendar', img: CalendarShot },
    { key: 'research', title: 'Start with real insight', desc: "Ask a question, get a research-backed answer in seconds — no tab-switching.", url: 'imergey.ai/research', img: ResearchShot },
    { key: 'writing', title: 'Write on-brand, with zero hassle', desc: 'Generate a publish-ready post from a single prompt. Adjust tone in one click.', url: 'imergey.ai/writing', img: WritingShot },
    { key: 'report', title: "Know what's working in real time", desc: 'Track followers, engagement, and reach in live view — double down on what works.', url: 'imergey.ai/reports', img: ReportShot },
  ];

  const testimonials = [
    { quote: "IMERGEY took our content output from sporadic to strategic. We now ship 3× more thought leadership with the same team — without losing the executive voice.", name: "Sarah B.", role: "CMO, Series C Tech Company", initials: "SB" },
    { quote: "The research-to-post workflow is exactly what we needed. Our CEO posts twice a week on LinkedIn and engagement is up 4× since we started using IMERGEY.", name: "Marcus T.", role: "Head of Brand, Financial Services", initials: "MT" },
    { quote: "We replaced four different tools with IMERGEY. The calendar, the AI assistant, the approval flow — all in one place. Our brand team hasn't looked back.", name: "Jana P.", role: "VP Marketing, Global Consultancy", initials: "JP" },
  ];

  return (
    <>
      <Layout className="imergey-layout">
        <HeaderIndex />
        <Content>

          {/* ── Hero ── */}
          <section className="hero-section">
            <div className="hero-grid-bg" />
            <div className="hero-banner-shape">
              <img src={DesignShape} alt="" />
            </div>
            <Container>
              <Reveal className="hero-content" as="div">
                <h1 className="hero-title">THE FUTURE OF BRAND <span>STRATEGY</span></h1>
                <p className="hero-subtitle">Plan, create, publish all from one AI-powered platform — unfair digital leverage for the world's best executives</p>
                <div className="platform-preview">
                  <div className="browser-frame">
                    <img src={DashboardShot} alt="Imergey dashboard" className="browser-frame-shot" />
                  </div>
                </div>
                <div className="hero-buttons">
                  <Button className="trial-btn" onClick={() => goToSignup()}>Start a free trial</Button>
                  <Button className="demo-btn" onClick={goToDemo}>
                    <span className="demo-btn-icon">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <rect x="3" y="5" width="18" height="16" rx="3" stroke="#3B6B94" strokeWidth="1.8"/>
                        <path d="M3 10h18" stroke="#3B6B94" strokeWidth="1.8"/>
                        <path d="M8 3v4M16 3v4" stroke="#3B6B94" strokeWidth="1.8" strokeLinecap="round"/>
                        <circle cx="15.5" cy="15" r="2.6" fill="#6CE5E8" stroke="#071E31" strokeWidth="1.2"/>
                      </svg>
                    </span>
                    <span>Book a Demo</span>
                  </Button>
                </div>
              </Reveal>

              <div className="features-container">
                <Reveal className="tranding-headding" as="div">
                  <h6>WHY TEAMS CHOOSE IMERGEY</h6>
                </Reveal>
                <Reveal className="trandings-insite" delay={80}>
                  <div className="feature-number"><div className="number-circle">1</div></div>
                  <h6>Plan your month with an all-in-one calendar — schedule and publish to social media in one seamless flow</h6>
                </Reveal>
                <Reveal className="trandings-insite" delay={160}>
                  <div className="feature-number"><div className="number-circle">2</div></div>
                  <h6>Purpose-built AI research and writing assistants that turn ideas into ready-to-post thought leadership in minutes — without losing your voice.</h6>
                </Reveal>
                <Reveal className="trandings-insite" delay={240}>
                  <div className="feature-number"><div className="number-circle">3</div></div>
                  <h6>Track performance in real time — every post, better than the last.</h6>
                </Reveal>
              </div>
            </Container>
          </section>

          {/* ── Plan / Scroll Story ── */}
          <section className="plan-section" id="whatwedo">
            <div className="plan-section-bg" />
            <Container>
              <Reveal className="plan-header" as="div">
                <h2 className="section-title plan-title">
                  <Reveal as="span" className="plan-word plan-word-gradient" delay={0}>PLAN</Reveal>
                  <span className="plan-sep" />
                  <Reveal as="span" className="plan-word plan-word-gradient" delay={90}>RESEARCH</Reveal>
                  <span className="plan-sep" />
                  <Reveal as="span" className="plan-word plan-word-gradient" delay={180}>PUBLISH</Reveal>
                  <span className="plan-sep" />
                  <Reveal as="span" className="plan-word plan-word-gradient" delay={270}>ACHIEVE</Reveal>
                </h2>
                <p>From first draft to published thought leadership, every step lives in one connected workspace — with strategic brand consistency & authority</p>
              </Reveal>
            </Container>
            <Container fluid>
              <ScrollStory steps={storySteps} />
            </Container>
          </section>

          {/* ── Social Proof ── (hidden — add real data before enabling) */}

          {/* ── Features Grid ── */}
          <section className="features-grid-section" id="howitwork">
            <Container>
              <Reveal className="section-header" as="div">
                <h2 className="section-title features-title">Designed for executives.{' '}<span className="features-title-accent">Built for results.</span></h2>
              </Reveal>
              <div className="feature-grid">
                <Reveal className="feature-grid-card bento-lg" delay={0}>
                  <div className="feature-icon-container">
                    <img src={CameraIcon} className="feature-icon" alt="" />
                  </div>
                  <h6 className="feature-title">User Management & Role-based Access</h6>
                  <p className="feature-text">Invite your whole team and control exactly who can create, approve, or publish — no shared logins required.</p>
                </Reveal>
                <Reveal className="feature-grid-card bento-sm" delay={100}>
                  <div className="feature-icon-container">
                    <img src={CalenderIcon} className="feature-icon" alt="" />
                  </div>
                  <h6 className="feature-title">Shared Scheduling & Approval Flows</h6>
                  <p className="feature-text">Plan weeks of content on one shared calendar, with approval flows that keep every post on-brand before it goes live.</p>
                </Reveal>
                <Reveal className="feature-grid-card bento-sm" delay={200}>
                  <div className="feature-icon-container">
                    <img src={DocumentIcon} className="feature-icon" alt="" />
                  </div>
                  <h6 className="feature-title">Document & Asset Management</h6>
                  <p className="feature-text">Keep brand guidelines, templates, and creative assets organized in one secure Vault your whole team can access.</p>
                </Reveal>
                <Reveal className="feature-grid-card bento-wide" delay={300}>
                  <div className="feature-icon-container">
                    <img src={ControlIcon} className="feature-icon" alt="" />
                  </div>
                  <h6 className="feature-title">Customizable Access Control</h6>
                  <p className="feature-text">Set granular permissions by role, brand, or workspace so every collaborator sees exactly what they need.</p>
                </Reveal>
              </div>
            </Container>
          </section>

          {/* ── Pricing ── (hidden — add real pricing before enabling) */}

          {/* ── FAQ ── */}
          <section className="faq-section" id="faq">
            <Container>
              <div className="faq-container">
                <Reveal className="faq-headding" as="div">
                  <h6 className="section-title">FAQ's</h6>
                  <h2 className="faq-subtitle">Frequently Asked Questions</h2>
                  <p>Can't find what you're looking for? Reach out to our team — we're happy to help you get the most out of IMERGEY.</p>
                </Reveal>
                <Collapse
                  className="faq-collapse"
                  bordered={false}
                  expandIconPosition="right"
                  expandIcon={({ isActive }) => isActive ? <MinusOutlined /> : <PlusOutlined />}
                  accordion
                >
                  {faqData.map((faq, index) => (
                    <Panel header={faq.question} key={index} className="faq-panel">
                      <Text className="faq-answer">{faq.answer}</Text>
                    </Panel>
                  ))}
                </Collapse>
              </div>
            </Container>
          </section>

          {/* ── Newsletter CTA ── */}
          <section className="newsletter-section">
            <Container>
              <Reveal className="newsletter-card" as="div">
                <div className="mail-icon-wrapper">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M13 2 4 14h6.5l-1.5 8 9-12h-6.5L13 2z" stroke="#6CA9E0" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                  </svg>
                </div>
                <h2 className="newsletter-title">Start your free trial. No credit card required.</h2>
                <p className="newsletter-desc">See how leading marketing teams stay ahead — put IMERGEY to work and experience it yourself.</p>
                <div className="newslatter-input">
                  <Input
                    className="input-inn"
                    placeholder="Enter your email..."
                    value={trialEmail}
                    onChange={(e) => setTrialEmail(e.target.value)}
                    onPressEnter={() => goToSignup(trialEmail)}
                  />
                  <Button className="subcribe-btn" onClick={() => goToSignup(trialEmail)}>
                    Get started for free
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Button>
                </div>
              </Reveal>
            </Container>
          </section>

          <FooterIndex />
        </Content>
      </Layout>

      <button
        className={`scroll-to-top-btn${showScrollTop ? ' visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </>
  );
}
